/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { PerPage } from "../constants/perPage";

const normalizeDropdownText = (value) => `${value ?? ""}`.replace(/\s+/g, " ").trim();

const hasDisplayableDropdownText = (value) => {
  return normalizeDropdownText(value).replace(/[\s,]/g, "").length > 0;
};

/**
 * @description Normalizes a restaurant group dropdown item into the dashboard shape.
 * @param restaurantGroup The raw restaurant group item returned by the API.
 * @returns The normalized restaurant group item.
 */
export const normalizeRestaurantGroup = (restaurantGroup) => ({
  ...restaurantGroup,
  label: restaurantGroup?.label ?? restaurantGroup?.id,
  value: restaurantGroup?.value ?? restaurantGroup?.name,
});

/**
 * @description Normalizes a restaurant dropdown item into the dashboard shape.
 * @param restaurant The raw restaurant item returned by the API.
 * @returns The normalized restaurant item.
 */
export const normalizeRestaurant = (restaurant) => ({
  ...restaurant,
  id: restaurant?.id ?? restaurant?.value,
  name: restaurant?.name ?? restaurant?.label,
});

/**
 * @description Normalizes a discount card dropdown item into the dashboard shape.
 * @param discountCard The raw discount card item returned by the API.
 * @returns The normalized discount card item.
 */
export const normalizeDiscountCard = (discountCard) => ({
  id: discountCard?.id ?? discountCard?.value,
  name: discountCard?.name ?? discountCard?.label,
});

/**
 * @description Normalizes an employee dropdown item and trims malformed text.
 * @param employee The raw employee item returned by the API.
 * @returns The normalized employee item.
 */
export const normalizeEmployee = (employee) => ({
  label: normalizeDropdownText(employee?.label ?? employee?.name),
  value: normalizeDropdownText(employee?.value ?? employee?.label ?? employee?.name),
});

/**
 * @description Determines whether a dropdown item has meaningful text to show.
 * @param value The dropdown text value to validate.
 * @returns True when the text contains more than whitespace and commas.
 */
export const hasDisplayableDropdownValue = (value) => {
  return hasDisplayableDropdownText(value);
};

/**
 * @description Merges two option arrays by a stable key, keeping the latest
 * value for duplicate keys while preserving previously loaded entries.
 * @param currentOptions The options already collected from earlier pages.
 * @param nextOptions The newly fetched options to merge in.
 * @param key The property name used to identify unique options.
 * @returns A merged array with duplicate keys collapsed to the newest entry.
 */
export const mergeUniqueByKey = (currentOptions = [], nextOptions = [], key) => {
  const optionsMap = new Map(currentOptions.map((option) => [option?.[key], option]));

  nextOptions.forEach((option) => {
    optionsMap.set(option?.[key], option);
  });

  return [...optionsMap.values()];
};

/**
 * @description Merges dropdown options only when the merged result would change
 * the list length, key ordering, or selected compared fields.
 * @param currentOptions The currently cached options.
 * @param nextOptions The newly fetched options to merge in.
 * @param key The stable key used to identify unique options.
 * @param comparedFields Additional fields to compare for change detection.
 * @returns The original array reference when nothing changed, otherwise the merged array.
 */
export const mergeOptionsIfChanged = (
  currentOptions = [],
  nextOptions = [],
  key,
  comparedFields = []
) => {
  const mergedOptions = mergeUniqueByKey(currentOptions, nextOptions, key);

  if (mergedOptions.length !== currentOptions.length) {
    return mergedOptions;
  }

  const hasChanges = mergedOptions.some((option, index) => {
    const currentOption = currentOptions[index];

    if (currentOption?.[key] !== option?.[key]) {
      return true;
    }

    return comparedFields.some((field) => currentOption?.[field] !== option?.[field]);
  });

  return hasChanges ? mergedOptions : currentOptions;
};

/**
 * @description Computes the next page number for dropdown infinite queries.
 * @param lastPage The last page of results returned by the API.
 * @param allPages All pages fetched so far.
 * @returns The next page number, or undefined when there are no more results.
 */
export const getDropdownNextPageParam = (lastPage, allPages) => {
  return lastPage.length >= PerPage ? allPages.length + 1 : undefined;
};
